import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { getEffectivePlan, PLAN_RANK } from '../utils/planAccess';

const PlanPreviewContext = createContext(null);

export function PlanPreviewProvider({ children }) {
  const { user } = useAuth();
  const [previewPlan, setPreviewPlan] = useState(null);
  const isOwner = user?.email === 'podracer500@hotmail.com';

  const enterPreview = useCallback((plan) => {
    if (!isOwner) return;
    setPreviewPlan(plan);
  }, [isOwner]);

  const exitPreview = useCallback(() => {
    setPreviewPlan(null);
  }, []);

  return (
    <PlanPreviewContext.Provider value={{
      previewPlan,          // null = not previewing
      isPreviewActive: !!previewPlan,
      enterPreview,
      exitPreview,
      isOwner,
    }}>
      {children}
    </PlanPreviewContext.Provider>
  );
}

export function usePlanPreview() {
  const context = useContext(PlanPreviewContext);
  if (!context) throw new Error('usePlanPreview must be used within PlanPreviewProvider');
  return context;
}

/**
 * Hook that returns effective plan access, respecting preview mode.
 * Use this in ALL page components that gate Pro features.
 */
export function useEffectiveAccess() {
  const { previewPlan, isPreviewActive } = usePlanPreview();
  const { user } = useAuth();

  return useMemo(() => {
    const effectivePlan = isPreviewActive ? previewPlan : getEffectivePlan(user);
    const rank = PLAN_RANK[effectivePlan] || 0;
    const isPro = rank >= (PLAN_RANK['professional'] || 0);
    return { effectivePlan, isPro, isPreviewActive };
  }, [previewPlan, isPreviewActive, user]);
}
